import { ClipboardDocumentListIcon } from "@heroicons/react/24/solid";
import { FunnelIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import Button2 from "../../../../../components/Button2";
import {
  forEdit,
  forReviewData,
  isApprovedInvoice,
  isDenyInvoice,
  isEditStateForReview,
  isRecallInvoice,
  selectedRowsForReview,
} from "../atoms/atom";
import { ForReviewDetails } from "./ForReviewDetails";
import { useRecallInvoiceDetails } from "../hooks/useRecalIInvoice";
import { useDenyInvoiceDetails } from "../hooks/useDenyInvoice";
import { useApprovedInvoiceDetails } from "../hooks/useApprovedInvoice";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import MaterialInvoiceTable from "../../../../../components/tables/MaterialInvoiceTable";
import { toast } from "../../../../../components/toastNotification";
import ForReviewTableFilters from "./TableFilter";
import { FIELDPERMISSIONS } from "../../../../PermissionsConstants";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import {
  isFromRediInvoice,
  voidedData,
} from "../../InvoiceCapture/atoms/atoms";
import { useLocation } from "react-router-dom";
import useGetActivityHistoryById from "../../InvoiceCapture/hooks/useGetActivityHistoryId";
import { IItemize } from "../../InvoiceCapture/types";
import { useGetForReviewInvoices } from "../hooks/useGetForReviewInvoices";
import { useGetForReviewInvoiceById } from "../hooks/useGetForReviewInvoiceById";
import { useGetVoidedInvoiceById } from "../../InvoiceCapture/hooks/useGetVoidedInvoiceById";

interface Props {}

export const ForReviewTable: React.FC<Props> = () => {
  const [invoiceTableData, setInvoiceTableData] = useState<any[]>([]);
  const [isShow, setIsShow] = useState(false);
  const [viewData, setViewData] = useState(false);
  const [, setRowForEdit] = useRecoilState(forEdit);
  const [, setIsEdit] = useRecoilState(isEditStateForReview);
  const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsForReview);
  const [filteredForReview, setFilteredForReview] = useState<any>([]);

  const [isSearching, setIsSearching] = useState(false);

  const RecallInvoice = useRecallInvoiceDetails();
  const DenyInvoice = useDenyInvoiceDetails();
  const ApprovedInvoice = useApprovedInvoiceDetails();
  const [recallOpenModal, setRecallOpenModal] = useRecoilState(isRecallInvoice);
  const [denyOpenModal, setDenyOpenModal] = useRecoilState(isDenyInvoice);
  const [approvedOpenModal, setApprovedOpenModal] =
    useRecoilState(isApprovedInvoice);
  const [filter, setFilter] = useState(false);
  const [, setForReviewInvoiceDetails] = useRecoilState<any>(forReviewData);
  const [, setVoidedInvoiceDetails] = useRecoilState<any>(voidedData);
  const [isFromRedirection, setIsFromRedirection] =
    useRecoilState(isFromRediInvoice);

  const approver = FIELDPERMISSIONS.fieldpermission_vendorinvoice_approver;
  const analyst = FIELDPERMISSIONS.fieldpermission_vendorinvoice_analyst;

  const userPermissions = useRecoilValue(permissionsSelector);

  const {
    data: invoiceData,
    isFetching: vendorInvoiceFetching,
    isSuccess: getVendorInvoiceSuccess,
  } = useGetForReviewInvoices();

  useEffect(() => {
    if (getVendorInvoiceSuccess && invoiceData) {
      setInvoiceTableData(invoiceData?.data);
    }
  }, [invoiceData]);

  const handleEdit = (res) => {
    setRowForEdit({
      ...res,
    });
    setIsEdit(true);
  };
  const getMaskedBankAccount = (
    bankAccount: string | undefined
  ): string | null => {
    return bankAccount ? bankAccount.replace(/.(?=.{4})/g, "x") : "N/A";
  };
  function formatDate(date) {
    if (!date) {
      return "N/A";
    }
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [month, day, year].join("/");
  }
  const getDescription = (rowData): string => {
    if (rowData?.length === 0) {
      return "Account#: N/A DUE N/A; Service N/A to N/A";
    }
    const autoPayDescription = rowData?.autoPayName
      ? rowData?.autoPayName?.toLowerCase() === "eft"
        ? getMaskedBankAccount(rowData?.bankAccount)
        : `AUTOPAY ${getMaskedBankAccount(rowData?.bankAccount)}`
      : "";
    const accountNumber = rowData?.accountNumber
      ? rowData?.accountNumber
      : "N/A";
    const dueDate = formatDate(rowData?.duedate);
    const servicePeriodDate = formatDate(rowData?.servicePeriodDate);
    const billingPeriodEndDate = formatDate(rowData?.billingPeriodEndDate);
    const serviceDateRange = `Service date ${servicePeriodDate} to ${billingPeriodEndDate} `;

    return `Account#: ${accountNumber} ${autoPayDescription} DUE ${dueDate}; ${serviceDateRange} `;
  };

  const onClickBatchId = (rowData) => {
    const description = getDescription(rowData);
    const itemizes = [] as Array<IItemize>;
    if (rowData?.itemizeDetails && rowData?.itemizeDetails?.length > 0) {
      const mappedItemized = rowData?.itemizeDetails?.map((x, index) => {
        return {
          ...x,
          unitCost: x.unitCost?.toFixed(2),
          extCost: x.extCost?.toFixed(2),
          amount: x.amount?.toFixed(2),
          isActive: true,
          parentRow: index > 0 ? false : true,
          tableName: "itemizeDetails",
        };
      });
      itemizes.push(...mappedItemized);
    }
    setIsShow(true);
    setViewData({
      ...rowData,
      itemizeDetails: itemizes,
      description: description,
    });
  };

  const onClickCancelShow = () => {
    setIsShow(false);
    setIsEdit(false);
    setRowForEdit(undefined);
  };

  const handleRecall = () => {
    const ids = selectedRows
      ? selectedRows?.map((x) => x.invoiceDetailsId)
      : undefined;
    RecallInvoice(ids);
    setSelectedRows([]);
    setRecallOpenModal(false);
  };

  const handleDeny = () => {
    const ids = selectedRows
      ? selectedRows?.map((x) => x.invoiceDetailsId)
      : undefined;
    DenyInvoice(ids);
    setSelectedRows([]);
    setDenyOpenModal(false);
  };

  const handleApproved = () => {
    const ids = selectedRows
      ? selectedRows?.map((x) => x.invoiceDetailsId)
      : undefined;
    ApprovedInvoice(ids);
    setSelectedRows([]);
    setApprovedOpenModal(false);
  };

  const handleSelectedRowsChange = (rows) => {
    const forInvoice = rows
      ?.filter((x) => x.isActive)
      ?.map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forInvoice);
  };

  const recallMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Recall ${
      selectedRows?.length
    } ${
      selectedRows
        ? selectedRows.length > 1
          ? "Invoice Details"
          : "Invoice Detail"
        : undefined
    } ?`}</div>
  );

  const denyMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Deny ${
      selectedRows?.length
    } ${
      selectedRows
        ? selectedRows.length > 1
          ? "Invoice Details"
          : "Invoice Detail"
        : undefined
    } ?`}</div>
  );

  const approvedMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Approved ${
      selectedRows?.length
    } ${
      selectedRows
        ? selectedRows.length > 1
          ? "Invoice Details"
          : "Invoice Detail"
        : undefined
    } ?`}</div>
  );

  const {
    data: forReviewInvoiceData,
    mutate: getForReviewInvoiceById,
    isSuccess: getForReviewSuccess,
    reset: resetGetForReviewInvoice,
  } = useGetForReviewInvoiceById();

  if (getForReviewSuccess && forReviewInvoiceData) {
    if (
      forReviewInvoiceData?.status === 201 ||
      forReviewInvoiceData?.status === 200
    ) {
      setForReviewInvoiceDetails(forReviewInvoiceData.data);
    }
    resetGetForReviewInvoice();
  }

  const {
    data: voidedInvoiceData,
    mutate: getVoidedInvoiceById,
    isSuccess: getVoidedSuccess,
    reset: resetGetVoidedInvoice,
  } = useGetVoidedInvoiceById();

  if (getVoidedSuccess && voidedInvoiceData) {
    if (
      voidedInvoiceData?.status === 201 ||
      voidedInvoiceData?.status === 200
    ) {
      setVoidedInvoiceDetails(voidedInvoiceData.data);
    }
    resetGetVoidedInvoice();
  }

  // View data from recent act
  let location = useLocation();

  let recordIdFromRecent;
  if (location.state != null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (
      recordIdFromRecent?.moduleName === "Vendor Invoice" &&
      invoiceTableData?.length > 0 &&
      isFromRedirection
    ) {
      const filteredData = invoiceTableData?.filter(
        (x) => x.vendorInvoiceId === Number(recordIdFromRecent.recordId)
      )[0];
      onClickBatchId(filteredData);
      const id = { Id: filteredData?.id };
      getForReviewInvoiceById(id);
      getactivityHistoryById(filteredData?.id);
      setIsFromRedirection(false);
    }
  }, [recordIdFromRecent, invoiceTableData]);
  const { data: activityHistoryData, mutate: getactivityHistoryById } =
    useGetActivityHistoryById();
  const matColumns = [
    {
      title: "Batch No.",
      field: "id",
      render: (rowData) => {
        return (
          <div
            className="blueAndBold underline cursor-pointer "
            onClick={() => {
              onClickBatchId(rowData);
              const id = { Id: rowData?.id };
              getForReviewInvoiceById(id);
              getVoidedInvoiceById(id);
              getactivityHistoryById(rowData?.id);
            }}
          >
            {rowData.id}
          </div>
        );
      },
    },
    {
      title: "Filename",
      field: "invoiceImageAttachment",
      render: (rowdata) => {
        return (
          <div className="truncate w-40">{rowdata.invoiceImageAttachment}</div>
        );
      },
    },
    {
      title: "Corporate Office No.",
      field: "storeNumber",
    },
    {
      title: "Bank Account",
      field: "bankAccount",
      render: (rowdata) => {
        return rowdata.bankAccount
          ? rowdata.bankAccount.replace(/.(?=.{4})/g, "x")
          : null;
      },
    },
    {
      title: "Bank Reference Date",
      field: "bankReferenceDate",
      render: (rowdata) => {
        return (
          <div>
            {rowdata.bankReferenceDate
              ? new Date(rowdata.bankReferenceDate).toLocaleDateString()
              : ""}
          </div>
        );
      },
    },
    {
      title: "Status Account No.",
      field: "accountNumber",
    },
    {
      title: "Date Created",
      field: "createdDate",
      render: (rowdata) => {
        return (
          <div>
            {rowdata.createdDate
              ? new Date(rowdata.createdDate).toLocaleDateString()
              : "unspecified"}
          </div>
        );
      },
    },
  ];

  const handleApprovedChecker = () => {
    var isValid = false;

    for (var x = 0; x < selectedRows?.length; x++) {
      if (
        selectedRows[x]?.bankReference === null ||
        selectedRows[x]?.bankReference === "" ||
        selectedRows[x]?.bankReferenceDate === null ||
        selectedRows[x]?.bankReferenceDate === ""
      ) {
        isValid = true;
      }
    }
    return isValid;
  };

  return (
    <>
      <div className=" ">
        {isShow === true ? (
          <div className="relative  ">
            <ForReviewDetails
              cancelShow={onClickCancelShow}
              rowData={viewData}
              handleEdit={handleEdit}
              setIsShow={setIsShow}
              activityHistoryData={activityHistoryData}
            />
          </div>
        ) : (
          <div className=" gap-2  bg-white min-h-fit">
            <div className="flex justify-between items-center py-2 ">
              <div>
                <div className="flex gap-2 items-center">
                  <div>
                    <ClipboardDocumentListIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                  </div>
                  <div className="flex flex-col leading tight">
                    <h3 className="text-xs font-medium text-gray-500">
                      Vendor Invoice Processing
                    </h3>
                    <h1 className="mr-auto font-bold text-primary text-md uppercase">
                      Batch List
                    </h1>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs  space-y-2">
              <div className="ml-auto flex justify-between pb-2 ">
                <div className="space-x-4 mr-6 ">
                  <button
                    className="hover:scale-90 transition duration-500 focus:bg-primary focus:text-white flex justify-center items-center shadow-md px-3 py-1 h-7 text-xs gap-x-2 font-semibold text-primary border hover:bg-primary selected hover:text-white border-primary rounded-md"
                    onClick={() => {
                      setFilter(!filter);
                    }}
                  >
                    <div>Filter By</div>
                    <FunnelIcon className="h-3 w-3 focus:bg-primary" />
                  </button>
                </div>
                <div className=" ">
                  <div className="flex gap-1">
                    {userPermissions.includes(analyst) && (
                      <>
                        <Button2
                          className={"opacity-100"}
                          disabled={false}
                          buttonText="Recall"
                          onClick={() => setRecallOpenModal(true)}
                        />
                      </>
                    )}
                    {userPermissions.includes(approver) && (
                      <>
                        <Button2
                          className={"opacity-100"}
                          disabled={false}
                          buttonText="Deny"
                          onClick={() => setDenyOpenModal(true)}
                        />
                        <Button2
                          className={"opacity-50"}
                          disabled={true}
                          buttonText="Approve"
                          onClick={() => {
                            if (handleApprovedChecker()) {
                              toast.fire(
                                "Information",
                                "Bank Reference Date and Bank Reference are required to submit to acumatica.",
                                "info"
                              );
                            } else {
                              setApprovedOpenModal(true);
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <main className="flex flex-row pt-2">
              {filter && (
                <div className="overflow-y-auto  flex-col flex-grow bg-white">
                  <ForReviewTableFilters
                    data={invoiceTableData}
                    setFilteredForReview={setFilteredForReview}
                    setIsSearching={setIsSearching}
                  />
                </div>
              )}

              <div
                className={
                  !filter
                    ? "false w-full h-5/6 bg-white overflow-x-auto box-shadow  "
                    : "w-4/5 h-5/6 bg-white overflow-y-auto box-shadow"
                }
              >
                <MaterialInvoiceTable
                  columns={matColumns}
                  data={isSearching ? filteredForReview : invoiceTableData}
                  isLoading={vendorInvoiceFetching}
                  selection={true}
                  selectedRows={selectedRows}
                  onSelectionChange={handleSelectedRowsChange}
                />
              </div>
            </main>
          </div>
        )}
      </div>
      {recallOpenModal && (
        <ConfirmBoxModal
          qConfirmation={recallMessage}
          isOpen={recallOpenModal}
          onClose={() => {
            setRecallOpenModal(false);
            setSelectedRows([]);
          }}
          onClickFunction={handleRecall}
          cancelButtonRef={undefined}
          ModalTitle={"Recall Invoice Details"}
        />
      )}
      {denyOpenModal && (
        <ConfirmBoxModal
          qConfirmation={denyMessage}
          isOpen={denyOpenModal}
          onClose={() => setDenyOpenModal(false)}
          onClickFunction={handleDeny}
          cancelButtonRef={undefined}
          ModalTitle={"Deny Invoice Details"}
        />
      )}
      {approvedOpenModal && (
        <ConfirmBoxModal
          qConfirmation={approvedMessage}
          isOpen={approvedOpenModal}
          onClose={() => setApprovedOpenModal(false)}
          onClickFunction={handleApproved}
          cancelButtonRef={undefined}
          ModalTitle={"Approved Invoice Details"}
        />
      )}
    </>
  );
};
